export type AiSearchPlatformSpoke = {
  schemaType?: "service" | "article";
  slug: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  platform: string;
  eyebrow: string;
  title: string;
  lead: string;
  metadataDescription: string;
  tldr: string;
  definition: string;
  heroStats: readonly { label: string; value: string }[];
  sections: readonly { title: string; body: string; points: readonly string[] }[];
  comparisonRows: readonly { signal: string; weak: string; stronger: string }[];
  auditFocus: readonly { title: string; summary: string }[];
  faqs: readonly { question: string; answer: string }[];
};

const sharedFaqs = (platform: string): readonly { question: string; answer: string }[] => [
  {
    question: `Can SEOH guarantee ${platform} citations or answer inclusion?`,
    answer:
      "No. SEOH does not claim control over generated answers, citations, rankings, platform access, or answer wording.",
  },
  {
    question: "What does the manual AI visibility audit produce?",
    answer:
      "The audit produces a practical gap map: prompts tested, pages reviewed, entity or schema issues, citation-readiness notes, and prioritized next steps.",
  },
  {
    question: "Does this replace traditional SEO?",
    answer:
      "No. GEO/AEO depends on traditional SEO foundations such as useful pages, clean internal links, crawlable content, structured data, and clear proof signals.",
  },
];

const sharedComparisonRows = [
  {
    signal: "Page answer",
    weak: "Broad marketing copy that does not answer a direct buyer question.",
    stronger: "A short answer, supporting detail, and a clear next step on the same page.",
  },
  {
    signal: "Entity clarity",
    weak: "Inconsistent service names, audience labels, or proof references across pages.",
    stronger: "Consistent brand, service, founder, audience, and proof language across the site.",
  },
  {
    signal: "Proof boundary",
    weak: "Unsupported awards, logos, testimonials, or visibility claims.",
    stronger: "Verified career proof, text-only worked-on properties, and no fabricated metrics.",
  },
] as const;

const coreFaqs = (topic: string): readonly { question: string; answer: string }[] => [
  {
    question: `Can SEOH guarantee results from ${topic} work?`,
    answer:
      "No. SEOH can improve clarity, structure, and review discipline, but it does not guarantee AI answers, citations, rankings, platform access, or generated wording.",
  },
  {
    question: "How does this connect to traditional SEO?",
    answer:
      "GEO/AEO depends on traditional SEO foundations: useful pages, crawlable content, internal links, schema, entity clarity, and honest proof signals.",
  },
  {
    question: "What is the next step after reading this?",
    answer:
      "Use the relevant checklist or request a manual AI visibility audit so Menashe can review the site, market, priority pages, and constraints.",
  },
];

const coreAiSearchSpokes: readonly AiSearchPlatformSpoke[] = [
  {
    schemaType: "article",
    slug: "what-is-geo",
    path: "/ai-search/what-is-geo",
    platform: "What is GEO",
    eyebrow: "GEO definition",
    title: "What is GEO?",
    lead:
      "GEO, or Generative Engine Optimization, is the work of making public content clearer, more structured, and easier for AI answer systems to understand and cite responsibly.",
    metadataDescription:
      "A practical definition of GEO from SEOH, covering generative engine optimization, entity clarity, answer-ready content, and citation-readiness limits.",
    tldr:
      "GEO means improving public pages so generative answer systems can better understand the brand, services, entities, claim context, and useful answers. It does not guarantee inclusion or citation.",
    definition:
      "Generative Engine Optimization is the process of improving owned content, entity signals, schema, internal links, and source clarity for AI-generated answer experiences.",
    heroStats: [
      { label: "Type", value: "Definition" },
      { label: "Scope", value: "AI answer readiness" },
      { label: "90-day order", value: "01" },
    ],
    sections: [
      {
        title: "GEO starts with clarity",
        body:
          "A generative answer system needs stable facts: who the brand serves, what the service does, where proof begins, and what claims are supported.",
        points: ["Brand and service entities", "Audience and use-case clarity", "Proof-safe wording"],
      },
      {
        title: "GEO is not a shortcut around SEO",
        body:
          "Content still needs crawlability, useful pages, internal links, and technical foundations before AI-search work can be credible.",
        points: ["Crawlable pages", "Internal links", "Structured content"],
      },
      {
        title: "GEO should avoid fake certainty",
        body:
          "No one controls generated answers. Good GEO work improves the material answer systems and users can evaluate.",
        points: ["Expectation clarity", "Legitimate citations", "Verified metrics"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Entity clarity", summary: "Check whether the brand, services, people, markets, and public claims are consistent." },
      { title: "Answer coverage", summary: "Find repeated buyer questions that priority pages do not answer directly." },
      { title: "Source readiness", summary: "Review whether pages can support clear, useful, citation-worthy summaries." },
    ],
    faqs: coreFaqs("GEO"),
  },
  {
    schemaType: "article",
    slug: "what-is-aeo",
    path: "/ai-search/what-is-aeo",
    platform: "What is AEO",
    eyebrow: "AEO definition",
    title: "What is AEO?",
    lead:
      "AEO, or Answer Engine Optimization, makes pages more useful for direct-answer experiences by pairing short answers with supporting detail, schema, and clear next steps.",
    metadataDescription:
      "A practical definition of AEO from SEOH, covering answer engine optimization, direct-answer content, FAQ structure, schema, and claim-aware next steps.",
    tldr:
      "AEO means structuring content so answer engines and users can extract clear, useful responses. The work supports featured answers, AI answers, FAQs, and zero-click search behavior.",
    definition:
      "Answer Engine Optimization is the practice of structuring content around direct questions, concise answers, supporting detail, schema, and clear user intent.",
    heroStats: [
      { label: "Type", value: "Definition" },
      { label: "Scope", value: "Answer structure" },
      { label: "90-day order", value: "02" },
    ],
    sections: [
      {
        title: "AEO answers the question first",
        body:
          "The page should answer the user's question clearly before asking them to interpret broad marketing copy.",
        points: ["Direct answers", "Helpful definitions", "Question-led sections"],
      },
      {
        title: "AEO needs support below the short answer",
        body:
          "Short answers work best when the page also includes context, examples, internal links, schema, and a useful next step.",
        points: ["Supporting detail", "FAQ and schema", "Clear next action"],
      },
      {
        title: "AEO is not answer control",
        body:
          "SEOH does not promise answer placement. The work improves the owned page so it can be understood and evaluated more easily.",
        points: ["Expectation clarity", "Supported claims", "Visible proof"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Question map", summary: "Group priority buyer questions by intent and page owner." },
      { title: "Short-answer quality", summary: "Check whether each page gives a clear answer before deeper explanation." },
      { title: "Schema alignment", summary: "Confirm FAQ or article schema matches visible page content." },
    ],
    faqs: coreFaqs("AEO"),
  },
  {
    schemaType: "article",
    slug: "geo-vs-seo",
    path: "/ai-search/geo-vs-seo",
    platform: "GEO vs SEO",
    eyebrow: "GEO vs SEO",
    title: "GEO vs SEO: what changes and what stays.",
    lead:
      "GEO adds AI-answer readiness to the same foundations that make SEO work: useful pages, crawlable structure, internal links, schema, and trustworthy proof.",
    metadataDescription:
      "Compare GEO vs SEO with SEOH: what changes for AI-search visibility, what traditional SEO still controls, and how to prioritize both responsibly.",
    tldr:
      "SEO helps pages rank, get crawled, and attract qualified search demand. GEO helps those pages become clearer source material for AI answers. The strongest strategy usually needs both.",
    definition:
      "GEO vs SEO compares AI-answer readiness against traditional search optimization, showing where entity clarity, answer structure, and citation readiness extend SEO foundations.",
    heroStats: [
      { label: "Type", value: "Comparison" },
      { label: "Scope", value: "SEO plus AI search" },
      { label: "90-day order", value: "03" },
    ],
    sections: [
      {
        title: "SEO still carries the foundation",
        body:
          "Technical health, useful content, internal links, and clear site architecture are still required before GEO work has enough material to improve.",
        points: ["Technical SEO", "Content usefulness", "Internal links"],
      },
      {
        title: "GEO adds answer readiness",
        body:
          "GEO asks whether the page can be summarized, cited, and understood in answer-led search experiences.",
        points: ["Entity clarity", "Direct answers", "Citation gaps"],
      },
      {
        title: "The work should be prioritized together",
        body:
          "A page with weak SEO foundations usually needs cleanup before AI-search refinements become meaningful.",
        points: ["Foundation first", "Then answer structure", "Then tracking"],
      },
    ],
    comparisonRows: [
      { signal: "Primary surface", weak: "SEO only or GEO only planned in isolation.", stronger: "Classic search and AI-answer visibility reviewed together." },
      { signal: "Page structure", weak: "Long copy with no direct answer or schema support.", stronger: "Clear answer, supporting detail, schema, and internal links." },
      { signal: "Proof", weak: "Unsupported metrics, logos, or claims.", stronger: "Verified context and restrained claims that match visible content." },
    ],
    auditFocus: [
      { title: "SEO foundation", summary: "Check crawl, indexation, links, templates, and content quality first." },
      { title: "AI-answer layer", summary: "Add definitions, FAQs, summaries, and citation-ready source sections where useful." },
      { title: "Measurement gap", summary: "Separate rankings, qualified inquiries, AI snapshots, and citation observations." },
    ],
    faqs: coreFaqs("GEO vs SEO"),
  },
  {
    schemaType: "article",
    slug: "geo-aeo-checklist",
    path: "/ai-search/geo-aeo-checklist",
    platform: "GEO/AEO checklist",
    eyebrow: "GEO/AEO checklist",
    title: "GEO/AEO checklist for AI-search readiness.",
    lead:
      "Use this checklist to review whether priority pages have clear entities, direct answers, schema, internal links, and claim-aware next steps.",
    metadataDescription:
      "A GEO/AEO checklist from SEOH for AI-search readiness, including entity clarity, direct answers, schema, internal links, and claim support.",
    tldr:
      "A useful GEO/AEO checklist reviews entity clarity, answer structure, schema eligibility, internal links, claim support, and measurement. It should not promise AI inclusion.",
    definition:
      "A GEO/AEO checklist is a repeatable review path for improving answer-readiness and AI-search clarity across priority pages.",
    heroStats: [
      { label: "Type", value: "Checklist" },
      { label: "Scope", value: "Page review" },
      { label: "90-day order", value: "04" },
    ],
    sections: [
      {
        title: "Start with the page job",
        body:
          "Every page should have a clear job: explain, compare, convert, reassure, or support a decision.",
        points: ["Primary intent", "Audience fit", "Conversion goal"],
      },
      {
        title: "Check answer and entity readiness",
        body:
          "The page should state who it helps, what it offers, why it is credible, and which questions it answers.",
        points: ["Entity facts", "Short answers", "FAQ coverage"],
      },
      {
        title: "Check structure and measurement",
        body:
          "Schema, internal links, and AI snapshot tracking should support the visible content rather than create hidden claims.",
        points: ["Schema eligibility", "Internal links", "AI snapshot notes"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Priority URLs", summary: "Choose the pages most likely to influence qualified inquiries." },
      { title: "Checklist pass", summary: "Review answer, entity, schema, link, proof, and conversion fields." },
      { title: "Next sprint", summary: "Turn the checklist into a short implementation queue." },
    ],
    faqs: coreFaqs("the GEO/AEO checklist"),
  },
  {
    schemaType: "article",
    slug: "ai-citation-building-101",
    path: "/ai-search/ai-citation-building-101",
    platform: "AI-citation building 101",
    eyebrow: "AI citations",
    title: "AI-citation building 101.",
    lead:
      "AI-citation work starts with clear owned pages, legitimate references, useful definitions, and repeatable tracking. It does not mean manufacturing fake sources.",
    metadataDescription:
      "AI-citation building 101 from SEOH, covering owned pages, legitimate references, source readiness, tracking, and no fake citation claims.",
    tldr:
      "AI-citation building means making pages and legitimate references easier to evaluate and cite. The work improves source readiness, but cannot force an AI system to cite a brand.",
    definition:
      "AI-citation building is the practice of improving source-ready owned content and legitimate external references for answer systems that display citations.",
    heroStats: [
      { label: "Type", value: "Educational spoke" },
      { label: "Scope", value: "Source readiness" },
      { label: "90-day order", value: "05" },
    ],
    sections: [
      {
        title: "Start with owned source pages",
        body:
          "A brand needs pages that explain topics, services, definitions, and supported claims well enough to function as source material.",
        points: ["Definitions", "Original explanations", "Supported claims"],
      },
      {
        title: "Use legitimate references only",
        body:
          "Citation work should never invent sources, fake metrics, or imply endorsements that do not exist.",
        points: ["Legitimate sources", "Supported authority", "Evidence-led claims"],
      },
      {
        title: "Track snapshots, not guarantees",
        body:
          "Citation behavior changes. A repeatable prompt and source snapshot is more useful than a one-time result.",
        points: ["Prompt set", "Source log", "Change notes"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Source inventory", summary: "List owned and legitimate third-party references by topic." },
      { title: "Citation gaps", summary: "Identify where no credible source exists for an important answer." },
      { title: "Tracking cadence", summary: "Set a repeatable prompt set and review cycle." },
    ],
    faqs: coreFaqs("AI-citation building"),
  },
  {
    schemaType: "article",
    slug: "entity-optimization-ai-search",
    path: "/ai-search/entity-optimization-ai-search",
    platform: "Entity optimization for AI search",
    eyebrow: "Entity optimization",
    title: "Entity optimization for AI search.",
    lead:
      "Entity optimization makes the brand, founder, services, audiences, markets, and proof context consistent enough for search and answer systems to interpret.",
    metadataDescription:
      "Entity optimization for AI search from SEOH, covering brand facts, service naming, founder context, past-work context, and consistency checks.",
    tldr:
      "Entity optimization for AI search means making names, services, people, audiences, markets, and public claims consistent across public pages and structured data.",
    definition:
      "Entity optimization is the process of clarifying and aligning the identifiable people, organizations, services, places, and concepts connected to a website.",
    heroStats: [
      { label: "Type", value: "Entity guide" },
      { label: "Scope", value: "Brand clarity" },
      { label: "90-day order", value: "06" },
    ],
    sections: [
      {
        title: "Entities need stable names",
        body:
          "A brand should use consistent names for the company, founder, services, industries, and resource paths.",
        points: ["Company facts", "Founder facts", "Service names"],
      },
      {
        title: "Claims need context",
        body:
          "Worked-on property names must be text-only context and should not imply endorsement, testimonial, or current client status.",
        points: ["Text-only context", "Permissioned logos", "Approved outcomes"],
      },
      {
        title: "Structured data should reinforce visible facts",
        body:
          "Schema helps when it describes content visitors can also see and verify on the page.",
        points: ["Visible facts", "Schema alignment", "Internal links"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Name consistency", summary: "Compare brand, founder, service, and industry labels across pages." },
      { title: "Schema consistency", summary: "Check whether structured data matches visible content." },
      { title: "Proof language", summary: "Confirm worked-on context is restrained and sourced." },
    ],
    faqs: coreFaqs("entity optimization"),
  },
  {
    schemaType: "article",
    slug: "schema-for-ai-engines",
    path: "/ai-search/schema-for-ai-engines",
    platform: "Schema for AI engines",
    eyebrow: "Schema for AI",
    title: "Schema for AI engines.",
    lead:
      "Schema helps search systems understand visible page facts when the markup stays aligned with approved claims and real authority signals.",
    metadataDescription:
      "Schema for AI engines from SEOH, covering schema eligibility, visible-content alignment, FAQ, Article, Service, Organization, and validation cadence.",
    tldr:
      "Schema for AI engines should describe real visible content: entities, breadcrumbs, services, FAQs, articles, products, and organization facts. It is support, not a shortcut.",
    definition:
      "Schema for AI engines is structured data that describes visible page content so search and answer systems can interpret the page more accurately.",
    heroStats: [
      { label: "Type", value: "Schema guide" },
      { label: "Scope", value: "Structured data" },
      { label: "90-day order", value: "07" },
    ],
    sections: [
      {
        title: "Schema starts with visible content",
        body:
          "Every schema field should be supported by content a visitor can see or a page fact the site genuinely controls.",
        points: ["Visible content", "Field mapping", "No hidden claims"],
      },
      {
        title: "Choose schema by page job",
        body:
          "A service page, FAQ, article, product page, and organization page each need different markup choices.",
        points: ["Service schema", "FAQ and Article", "BreadcrumbList"],
      },
      {
        title: "Validation is part of governance",
        body:
          "Schema should be retested after CMS, copy, template, or navigation changes so stale markup does not create risk.",
        points: ["Validation cadence", "Template review", "Change log"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Schema inventory", summary: "List active schema by template and URL type." },
      { title: "Eligibility check", summary: "Confirm whether visible content supports every schema field." },
      { title: "Validation pass", summary: "Run validation and map fixes to the next sprint." },
    ],
    faqs: coreFaqs("schema for AI engines"),
  },
  {
    schemaType: "article",
    slug: "zero-click-search-strategy",
    path: "/ai-search/zero-click-search-strategy",
    platform: "Zero-click search strategy",
    eyebrow: "Zero-click strategy",
    title: "Zero-click search strategy.",
    lead:
      "Zero-click strategy prepares for searches where the user may get an answer before visiting a site, while still giving qualified visitors a reason to continue.",
    metadataDescription:
      "Zero-click search strategy from SEOH, covering direct answers, brand recall, AI summaries, snippets, internal CTAs, and qualified inquiry paths.",
    tldr:
      "Zero-click strategy means answering clearly enough to earn trust even when the click is delayed, then giving qualified visitors a reason to continue when they need depth.",
    definition:
      "Zero-click search strategy is the planning process for searches where answers, snippets, maps, or AI summaries may satisfy part of the user's need before a website visit.",
    heroStats: [
      { label: "Type", value: "Strategy guide" },
      { label: "Scope", value: "Answer-led journeys" },
      { label: "90-day order", value: "08" },
    ],
    sections: [
      {
        title: "Not every useful search becomes a click",
        body:
          "Some queries build trust, recall, or shortlist inclusion before the user is ready to visit or enquire.",
        points: ["Brand recall", "Shortlist intent", "Assisted journeys"],
      },
      {
        title: "Pages still need next steps",
        body:
          "A page should answer the immediate question and then offer deeper context, a resource, or a manual review path.",
        points: ["Short answer", "Depth below", "Clear CTA"],
      },
      {
        title: "Measure more than raw traffic",
        body:
          "Zero-click work should track qualified inquiries, branded search, AI snapshots, resource requests, and assisted conversion signals.",
        points: ["Qualified inquiries", "Brand demand", "AI snapshots"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Query split", summary: "Separate answer-only, research, comparison, and conversion-intent searches." },
      { title: "Page next step", summary: "Confirm each answer-led page has a deeper path for qualified visitors." },
      { title: "Assisted metrics", summary: "Track indicators beyond sessions and clicks." },
    ],
    faqs: coreFaqs("zero-click strategy"),
  },
  {
    schemaType: "article",
    slug: "ai-ready-content-frameworks",
    path: "/ai-search/ai-ready-content-frameworks",
    platform: "AI-ready content frameworks",
    eyebrow: "AI-ready content",
    title: "AI-ready content frameworks.",
    lead:
      "AI-ready content uses direct answers, definitions, structured sections, FAQs, comparison points, and supported claims to make pages easier to interpret.",
    metadataDescription:
      "AI-ready content frameworks from SEOH, covering direct answers, definitions, FAQs, comparison tables, claim support, and internal-link structure.",
    tldr:
      "AI-ready content frameworks make pages easier to parse: answer first, define terms, support claims, structure comparisons, add FAQs, and link to the next useful page.",
    definition:
      "An AI-ready content framework is a repeatable page structure that improves answer extraction, entity clarity, proof safety, and user usefulness.",
    heroStats: [
      { label: "Type", value: "Framework" },
      { label: "Scope", value: "Content structure" },
      { label: "90-day order", value: "09" },
    ],
    sections: [
      {
        title: "Answer first, then explain",
        body:
          "A useful page gives the direct answer near the top, then expands with context, examples, constraints, and internal links.",
        points: ["Short answer", "Supporting detail", "Next step"],
      },
      {
        title: "Use repeatable content blocks",
        body:
          "Definitions, TL;DRs, comparisons, FAQ sections, and proof notes help both users and answer systems follow the page.",
        points: ["Definitions", "Comparison tables", "FAQs"],
      },
      {
        title: "Keep claims inside evidence",
        body:
          "AI-ready content works best when numbers, testimonials, claims, and brand references are visible, permissioned, and supportable.",
        points: ["Evidence-led proof", "Supported numbers", "Permissioned brand references"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Template scan", summary: "Check whether priority templates support AI-ready blocks." },
      { title: "Content rewrite map", summary: "Prioritize pages that need direct answers, definitions, or proof cleanup." },
      { title: "Internal links", summary: "Connect definitions, service pages, industry pages, and resources." },
    ],
    faqs: coreFaqs("AI-ready content frameworks"),
  },
  {
    schemaType: "article",
    slug: "measuring-ai-citation-success",
    path: "/ai-search/measuring-ai-citation-success",
    platform: "Measuring AI-citation success",
    eyebrow: "AI measurement",
    title: "Measuring AI-citation success.",
    lead:
      "AI-citation measurement should combine repeatable snapshots, source logs, ranking context, resource demand, and qualified inquiry signals without pretending AI answers are fully controllable.",
    metadataDescription:
      "Measure AI-citation success with SEOH's practical framework for prompt snapshots, source logs, schema coverage, resource demand, and qualified inquiries.",
    tldr:
      "AI-citation success is measured with repeatable prompt snapshots, source visibility, owned-page readiness, citation quality, and business signals such as qualified inquiries.",
    definition:
      "Measuring AI-citation success is the practice of tracking citation snapshots, source quality, owned-page readiness, and downstream business signals over time.",
    heroStats: [
      { label: "Type", value: "Measurement guide" },
      { label: "Scope", value: "Baseline and trend" },
      { label: "90-day order", value: "10" },
    ],
    sections: [
      {
        title: "Start with repeatable snapshots",
        body:
          "Use the same prompt set, market, date, and notes so changes can be compared without overreading one answer.",
        points: ["Prompt set", "Date and market", "Answer notes"],
      },
      {
        title: "Separate citation quality from business value",
        body:
          "A citation can be present but low-value. The measurement plan should also watch qualified inquiries, resource requests, and branded demand.",
        points: ["Citation quality", "Qualified inquiries", "Resource requests"],
      },
      {
        title: "Document limits clearly",
        body:
          "AI outputs change and may vary by user, location, platform, and timing. Reports should explain that uncertainty.",
        points: ["No guarantee language", "Snapshot caveats", "Trend review"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Prompt baseline", summary: "Create a small repeatable query set before new content ships." },
      { title: "Citation log", summary: "Record cited sources, missing owned pages, and claim quality." },
      { title: "Decision rule", summary: "Define when content, schema, links, or reputation work should change." },
    ],
    faqs: coreFaqs("AI-citation measurement"),
  },
];

export const AI_SEARCH_PLATFORM_SPOKES: readonly AiSearchPlatformSpoke[] = [
  ...coreAiSearchSpokes,
  {
    slug: "google-ai-overviews-optimization",
    path: "/ai-search/google-ai-overviews-optimization",
    platform: "Google AI Overviews",
    eyebrow: "Google AI Overviews optimization",
    title: "Google AI Overviews optimization planning.",
    lead:
      "SEOH reviews whether your public pages, entities, and citations give Google enough clear context to understand the brand and summarize it responsibly.",
    metadataDescription:
      "Google AI Overviews optimization from SEOH, including entity clarity, content structure, citation review, schema, and manual audit next steps.",
    tldr:
      "Google AI Overviews optimization means making owned pages easier to understand, verify, and summarize. It does not guarantee inclusion or wording.",
    definition:
      "Google AI Overviews optimization is the process of improving page clarity, entity consistency, structured data, and citation readiness for queries where Google may generate summary answers.",
    heroStats: [
      { label: "Surface", value: "AI summaries" },
      { label: "Focus", value: "Entity clarity" },
      { label: "CTA", value: "Manual audit" },
    ],
    sections: [
      {
        title: "How AI Overviews change SEO work",
        body:
          "AI Overviews can compress several sources into a short answer. The work starts by making brand pages, service language, and third-party references easier to interpret.",
        points: ["Clear service definitions", "Consistent entity signals", "Useful supporting citations"],
      },
      {
        title: "What SEOH checks first",
        body:
          "The audit checks whether current pages answer buyer questions clearly and whether external signals support the claims being made.",
        points: ["Page structure", "Question coverage", "Citation gaps"],
      },
      {
        title: "What stays off the page",
        body:
          "No agency can guarantee inclusion, wording, ranking, or citation behavior in AI Overviews. The practical goal is better clarity and stronger retrieval signals.",
        points: ["Expectation clarity", "Legitimate citations", "Verified visibility metrics"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Entity snapshot", summary: "How clearly the brand, services, locations, and proof signals are represented." },
      { title: "Question coverage", summary: "Which buyer questions are answered cleanly enough to be useful in summaries." },
      { title: "Citation readiness", summary: "Where third-party references, structured content, or clearer pages may be needed." },
    ],
    faqs: sharedFaqs("Google AI Overviews"),
  },
  {
    slug: "chatgpt-seo",
    path: "/ai-search/chatgpt-seo",
    platform: "ChatGPT",
    eyebrow: "ChatGPT SEO",
    title: "ChatGPT SEO and answer-readiness review.",
    lead:
      "SEOH reviews how clearly your public content explains the brand so answer systems can understand the offer, audience, proof, and constraints.",
    metadataDescription:
      "ChatGPT SEO and visibility review from SEOH, covering answer-readiness, entity clarity, content gaps, and manual AI visibility audit next steps.",
    tldr:
      "ChatGPT SEO is answer-readiness work: clearer pages, consistent entities, useful definitions, and claim-aware content that can be summarized responsibly.",
    definition:
      "ChatGPT SEO is the practice of making public brand and service content easier for ChatGPT-style answer systems and users to understand, retrieve, compare, and cite.",
    heroStats: [
      { label: "Surface", value: "Answer systems" },
      { label: "Focus", value: "Retrieval-ready pages" },
      { label: "CTA", value: "Manual audit" },
    ],
    sections: [
      {
        title: "Why answer-readiness matters",
        body:
          "People use ChatGPT-style tools to compare options, clarify terms, and shortlist vendors. Confusing or thin public content can make the brand harder to describe.",
        points: ["Offer clarity", "Audience fit", "Supported claims"],
      },
      {
        title: "What SEOH can assess",
        body:
          "The audit maps whether important pages describe the business in a way that is specific, consistent, and easy to cite or summarize.",
        points: ["Brand facts", "Service pages", "FAQ and comparison gaps"],
      },
      {
        title: "What stays off the page",
        body:
          "SEOH does not claim control over ChatGPT responses. The work improves the public material that answer systems may use or users may reference.",
        points: ["Expectation clarity", "Real buyer prompts", "Supported performance claims"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Prompt sample", summary: "A small set of buyer prompts used to find clarity gaps and mismatches." },
      { title: "Content gap map", summary: "Pages or sections that should answer repeated questions more directly." },
      { title: "Entity consistency", summary: "Brand, service, audience, and proof signals checked for contradictions." },
    ],
    faqs: sharedFaqs("ChatGPT"),
  },
  {
    slug: "perplexity-optimization",
    path: "/ai-search/perplexity-optimization",
    platform: "Perplexity",
    eyebrow: "Perplexity optimization",
    title: "Perplexity optimization and citation readiness.",
    lead:
      "SEOH reviews whether your public pages are clear, specific, and source-ready enough to support cited answer experiences.",
    metadataDescription:
      "Perplexity optimization from SEOH, including source clarity, page structure, citation gaps, and manual audit next steps.",
    tldr:
      "Perplexity optimization focuses on source-ready pages: direct answers, clear claims, useful structure, and honest references.",
    definition:
      "Perplexity optimization is the process of improving owned content so cited answer experiences have clearer, more useful source material to evaluate.",
    heroStats: [
      { label: "Surface", value: "Cited answers" },
      { label: "Focus", value: "Source readiness" },
      { label: "CTA", value: "Manual audit" },
    ],
    sections: [
      {
        title: "Why source clarity matters",
        body:
          "Citation-led answer experiences need pages that make claims, definitions, and supporting context easy to understand without relying on vague marketing copy.",
        points: ["Specific claims", "Useful definitions", "Supportive page structure"],
      },
      {
        title: "What SEOH can assess",
        body:
          "The audit looks for pages that can support factual answer snippets and identifies where clearer source material may be needed.",
        points: ["Citation-worthy sections", "Thin or duplicated claims", "Missing proof context"],
      },
      {
        title: "What stays off the page",
        body:
          "SEOH cannot guarantee Perplexity citations or answer placement. The work focuses on improving the material available to users and answer systems.",
        points: ["Expectation clarity", "Legitimate sources", "Supported authority"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Source audit", summary: "Which pages are most likely to support clear factual references." },
      { title: "Claim cleanup", summary: "Where copy should become more specific, restrained, or supported." },
      { title: "Reference gaps", summary: "Topics that may need better owned pages or legitimate third-party references." },
    ],
    faqs: sharedFaqs("Perplexity"),
  },
  {
    slug: "gemini-optimization",
    path: "/ai-search/gemini-optimization",
    platform: "Gemini",
    eyebrow: "Gemini optimization",
    title: "Gemini optimization and brand clarity review.",
    lead:
      "SEOH reviews whether your public pages give enough structured, consistent context for Gemini-style answer experiences and Google-adjacent discovery paths.",
    metadataDescription:
      "Gemini optimization from SEOH, covering structured content, entity clarity, answer gaps, and manual AI visibility audit next steps.",
    tldr:
      "Gemini optimization improves structured clarity around your brand, services, proof, definitions, and buyer questions.",
    definition:
      "Gemini optimization is the process of making public content more structured, consistent, and answerable for Gemini-style discovery and Google-adjacent AI experiences.",
    heroStats: [
      { label: "Surface", value: "AI answers" },
      { label: "Focus", value: "Structured clarity" },
      { label: "CTA", value: "Manual audit" },
    ],
    sections: [
      {
        title: "Why structured clarity matters",
        body:
          "Answer systems work better when pages define the offer, audience, service boundaries, and evidence in a consistent way.",
        points: ["Clear headings", "Consistent service language", "Question-led sections"],
      },
      {
        title: "What SEOH can assess",
        body:
          "The audit checks whether important pages can answer buyer questions without forcing users or systems to infer missing context.",
        points: ["Entity and service clarity", "Schema and page sections", "Internal link support"],
      },
      {
        title: "Expectations stay clear",
        body:
          "SEOH does not claim control over Gemini answers. The work improves the public signals that may help answer-readiness.",
        points: ["Answer limits stated", "Evidence-led proof", "Supported claims"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Structured page review", summary: "How headings, sections, schema, and internal links support answer clarity." },
      { title: "Question gap list", summary: "Buyer questions that should be answered more directly." },
      { title: "Internal link map", summary: "Where related pages should support the main AI-search story." },
    ],
    faqs: sharedFaqs("Gemini"),
  },
  {
    slug: "bing-copilot-optimization",
    path: "/ai-search/bing-copilot-optimization",
    platform: "Bing Copilot",
    eyebrow: "Bing Copilot optimization",
    title: "Bing Copilot optimization and answer context review.",
    lead:
      "SEOH reviews whether your site and supporting signals give Copilot-style answer experiences enough clear context about the brand, services, and public claims.",
    metadataDescription:
      "Bing Copilot optimization from SEOH, including answer context, page clarity, citation gaps, and manual AI visibility audit next steps.",
    tldr:
      "Bing Copilot optimization improves answer context across owned pages, search signals, internal links, and citation-ready content.",
    definition:
      "Bing Copilot optimization is the process of improving public content and search context for Microsoft AI search and Copilot-style generated answers.",
    heroStats: [
      { label: "Surface", value: "Copilot answers" },
      { label: "Focus", value: "Context and citations" },
      { label: "CTA", value: "Manual audit" },
    ],
    sections: [
      {
        title: "Why answer context matters",
        body:
          "Copilot-style answers can blend search context and generated summaries. Clear pages make it easier for users to validate what the brand actually offers.",
        points: ["Search context", "Service definitions", "Useful supporting pages"],
      },
      {
        title: "What SEOH can assess",
        body:
          "The audit reviews whether your content and internal structure support clear answer paths for commercial, local, service, or reputation questions.",
        points: ["Page clarity", "Internal links", "Citation and entity gaps"],
      },
      {
        title: "What stays off the page",
        body:
          "SEOH cannot guarantee Copilot wording, citations, or ranking behavior. The work focuses on improving public content and discoverable context.",
        points: ["Expectation clarity", "Legitimate citations", "Ranking limits stated"],
      },
    ],
    comparisonRows: sharedComparisonRows,
    auditFocus: [
      { title: "Answer path map", summary: "How users and answer systems move from broad questions to service-specific pages." },
      { title: "Content support check", summary: "Where owned content should define terms, scope, and evidence more clearly." },
      { title: "Risk notes", summary: "Claims that should be softened, supported, or removed before AI-search work expands." },
    ],
    faqs: sharedFaqs("Bing Copilot"),
  },
];

export function getAiSearchPlatformSpoke(slug: string): AiSearchPlatformSpoke | undefined {
  return AI_SEARCH_PLATFORM_SPOKES.find((spoke) => spoke.slug === slug);
}
